// Project entity — see FD §5 and §13 (Projecten).

import type { BudgetLimits, RetentionPolicy } from './workspace.js'
import type { WorkflowDefinition } from './workflow.js'

export interface ProjectRepository {
  id: string
  url: string
  defaultBranch: string
  isDefault: boolean
}

/**
 * One workflow state in a project's flow policy, mirroring the YAML example
 * in FD §13.2 (`workflow.<state>.agent_role/requires/blocks`).
 *
 * @deprecated Legacy flat view of the policy's states, kept while the
 * policy engine's gate check still reads it. New code uses
 * `ProjectPolicy.workflows` (`WorkflowDefinition`, types/workflow.ts).
 */
export interface ProjectWorkflowState {
  name: string
  agentRole: string | null
  requires: string[]
  blocks: string[]
  freshContext?: boolean
}

/**
 * The centralized flow/safety rules for a project (FD §13.2). Parsed from
 * (or serialized to) the project's `project-policy.yaml`.
 */
export interface ProjectPolicy {
  project: string
  version: number
  /**
   * @deprecated Flattened states of every workflow (default workflow first,
   * first occurrence of a state name wins). Derived from `workflows`.
   */
  workflow: ProjectWorkflowState[]
  /** Per-task-type workflows (`workflows:` in the YAML; legacy `workflow:` normalizes to key 'default'). */
  workflows: WorkflowDefinition[]
  /** Key of the workflow used when a task names none; null when there are no workflows. */
  defaultWorkflow: string | null
  humanApproval: string[]
  forbidden: string[]
}

/**
 * How a project specifies its changes (docs/plans/2026-10-04-openspec.md).
 * 'none': no method. 'openspec': changes live as OpenSpec proposals in the
 * repository's `openspec/` folder and agents get the OpenSpec work method.
 */
export const SPEC_MODES = ['none', 'openspec'] as const
export type SpecMode = (typeof SPEC_MODES)[number]

export interface Project {
  id: string
  workspaceId: string
  /** Short key for ticket numbers ('GOB'); unique per workspace. */
  key: string
  name: string
  description: string | null
  repositories: ProjectRepository[]
  documentationSources: string[]
  architectureRules: string | null
  securityRules: string | null
  codingConventions: string | null
  /** The project's specification method; 'none' unless switched on. */
  specMode: SpecMode
  policy: ProjectPolicy
  definitionOfReady: string[]
  definitionOfDone: string[]
  mcpServiceIds: string[]
  allowedAgentIds: string[]
  projectMemoryCollectionId: string | null
  repositoryAllowlist: string[]
  networkAllowlist: string[]
  budgets: BudgetLimits
  retentionPolicy: RetentionPolicy
  createdAt: string
  updatedAt: string
}

// TODO: contextcompiler output type (project summary + selected rules/skills/
// memory/sources/tools for a single run) — see FD §13.3. Depends on Skill,
// MemoryEntry and Task types; revisit once memory types are scaffolded in a
// later task.
